import { HTTPInstanceWithPrefix } from '@/lib/http/http-instance-with-prefix'
import {
  DeleteTablesDto,
  FloorPlanRoomDto,
  GetTableDto,
  TableStatus
} from './FloorPlanAPI.model'

/**
 * REST client for the floor-plan resource. Endpoints are prefixed with
 * `/restaurants/{restaurantId}` by HTTPInstanceWithPrefix.
 *
 * The backend exposes the floor plan as ONE document: a list of rooms with
 * their tables nested inside. Reads and writes are whole-plan, there is no
 * granular per-room / per-table create endpoint. The mutation layer serialises
 * the current store into this shape on every change (see useFloorPlanMutations
 * + floorPlanMapper).
 */
class FloorPlanAPI {
  /**
   * GET the whole floor plan (rooms with nested tables).
   *
   * The OpenAPI spec annotates the response as a single FloorPlanRoomDto but
   * the resource is a collection, so we normalise to an array to be safe
   * whether the backend returns one object or a list.
   */
  static getFloorPlan = (): Promise<FloorPlanRoomDto[]> =>
    HTTPInstanceWithPrefix.get('/floor-plan').then(res => {
      const data = res.data as FloorPlanRoomDto[] | FloorPlanRoomDto | null
      if (Array.isArray(data)) return data
      return data ? [data] : []
    })

  /**
   * PUT the whole floor plan. This replaces the stored plan, rooms/tables not
   * present in the payload are removed.
   */
  static updateFloorPlan = (
    rooms: FloorPlanRoomDto[]
  ): Promise<FloorPlanRoomDto[]> =>
    HTTPInstanceWithPrefix.put('/floor-plan', rooms).then(res => {
      const data = res.data as FloorPlanRoomDto[] | FloorPlanRoomDto | null
      if (Array.isArray(data)) return data
      return data ? [data] : rooms
    })

  // --- operational table endpoints (POS / QR side, not the geometry) --------

  static getTables = (): Promise<GetTableDto[]> =>
    HTTPInstanceWithPrefix.get('/tables').then(res => res.data)

  static updateTableStatus = (id: string, status: TableStatus) =>
    HTTPInstanceWithPrefix.put(`/tables/${id}`, { status })

  static deleteTables = (ids: DeleteTablesDto['ids']) =>
    HTTPInstanceWithPrefix.delete('/tables', { data: { ids } })
}

export default FloorPlanAPI
